import { useCallback, useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../../styles/components/Navbar.module.css";
import ChoiceChip from "../../ui/selectors/ChoiceChip";
import NotificationsBell from "../../ui/notifications/NotificationsBell";
import NotificationsModal from "../notifications/NotificationsModal";
import SettingsModal from "../../components/settings/SettingsModal";
import RestartChainModal from "../mails/RestartChainModal";

import { ReactComponent as DashboardIcon } from "../../assets/icons/dashboard.svg";
import { ReactComponent as AccountsIcon } from "../../assets/icons/accounts.svg";
import { ReactComponent as UsersIcon } from "../../assets/icons/users.svg";
import { ReactComponent as LeadsIcon } from "../../assets/icons/leads.svg";
import { ReactComponent as MailsIcon } from "../../assets/icons/mails.svg";
import { ReactComponent as InboxIcon } from "../../assets/icons/inbox.svg";
import { ReactComponent as TemplatesIcon } from "../../assets/icons/templates.svg";
import { ReactComponent as DatabaseIcon } from "../../assets/icons/database.svg";
import { ReactComponent as LogoIcon } from "../../assets/icons/logoMail.svg";
import { ReactComponent as CreditsIcon } from "../../assets/icons/credits.svg";

import { useAuth } from "../../contexts/AuthContext";
import { validationService } from "../../services/validation.service";
import { mailsService } from "../../services/mails.service";
import { subscribeToUnassignedPendingCount } from "../../utils/unassignedMailsEvents";
import { useNotifications } from "../../contexts/NotificationsContext";
import { getInitials } from "../../utils/getInitials";
import {
  CREDIT_KPI_CODES,
  CREDIT_CHIP_GRADIENTS,
  CREDIT_KPI_LABELS,
} from "../../constants/creditKpis";

const CREDIT_CAROUSEL_INTERVAL_MS = 4000;

const Navbar = () => {
  const { auth, activeAccountId } = useAuth();
  const isSuperAdmin = !!auth?.user?.is_account_admin;

  const [imgError, setImgError] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState(null);
  const [creditKpis, setCreditKpis] = useState([]);
  const [visibleCreditIndex, setVisibleCreditIndex] = useState(0);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const [chainFinishedNotification, setChainFinishedNotification] =
    useState(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unassignedPendingCount, setUnassignedPendingCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const currentSection = location.pathname.split("/")[1] || "";
  const requestedSettingsSection = new URLSearchParams(location.search).get("configuracion");

  useEffect(() => {
    if (requestedSettingsSection) {
      setSettingsSection(requestedSettingsSection);
      setSettingsOpen(true);
    } else {
      setSettingsOpen(false);
    }
  }, [location.key, requestedSettingsSection]);

  const closeSettings = () => {
    setSettingsOpen(false);
    setSettingsSection(null);
    if (requestedSettingsSection) {
      const params = new URLSearchParams(location.search);
      params.delete("configuracion");
      const search = params.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
    }
  };

  useEffect(() => {
    let cancelled = false;
    mailsService
      .fetchUnassignedMails({ limit: 1 })
      .then((response) => {
        if (!cancelled) setUnassignedPendingCount(response?.pending_count ?? 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentSection]);

  useEffect(
    () => subscribeToUnassignedPendingCount(setUnassignedPendingCount),
    [],
  );

  useEffect(() => {
    if (!activeAccountId) return;
    let cancelled = false;
    Promise.allSettled(
      CREDIT_KPI_CODES.map((kpiCode) =>
        validationService
          .getCredits(activeAccountId, kpiCode)
          .then((kpiData) => ({ code: kpiCode, ...kpiData })),
      ),
    ).then((results) => {
      if (cancelled) return;
      // Las cuentas pueden no tener configurados todos los KPIs: se muestran solo los que respondieron.
      setCreditKpis(
        results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value),
      );
      setVisibleCreditIndex(0);
    });
    return () => {
      cancelled = true;
    };
  }, [activeAccountId]);

  useEffect(() => {
    if (creditKpis.length < 2 || carouselPaused) return;
    const intervalId = setInterval(() => {
      setVisibleCreditIndex(
        (previousIndex) => (previousIndex + 1) % creditKpis.length,
      );
    }, CREDIT_CAROUSEL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [creditKpis, carouselPaused]);

  const name = auth?.user?.name || "";
  const userName = auth?.user?.username || "";
  const userImg = auth?.user?.avatar || null;

  const displayName = useMemo(
    () => name?.trim() || userName || "",
    [name, userName],
  );

  const { notifications, unreadCount, markAsRead, markAllAsRead } =
    useNotifications();

  const visibleCreditKpi = creditKpis[visibleCreditIndex] ?? null;

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read) {
        await markAsRead(notification.id);
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }

    if (notification.notification_type?.code === "mail_chain_finished") {
      setChainFinishedNotification(notification);
      return;
    }

    if (notification.notification_type?.code === "follow_up_reminder") {
      const payload = notification.payload || {};
      if (payload.lead_id && payload.subject_group_id) {
        try {
          await mailsService.clearConversationReminder({
            leadId: payload.lead_id,
            subjectGroupId: payload.subject_group_id,
            reminderId: payload.reminder_id,
          });
        } catch (error) {
          console.error("Error clearing the follow-up reminder:", error);
        }
      }
    }

    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
  };

  const openNotificationsModal = useCallback(() => {
    setNotificationsOpen(true);
  }, []);

  const closeNotificationsModal = useCallback(() => {
    setNotificationsOpen(false);
  }, []);

  const handleNotificationOpenFromModal = (notification) => {
    setNotificationsOpen(false);
    return handleNotificationClick(notification);
  };

  return (
    <>
      <div className={styles.navbar}>
        <div className={styles.left}>
          <div className={styles.logo}>
            <LogoIcon />
          </div>
        </div>

        <div className={styles.center}>
          <ChoiceChip
            Icon={DashboardIcon}
            label="Dashboard"
            to="/dashboard"
            height={24}
          />
          <ChoiceChip Icon={LeadsIcon} label="Leads" to="/leads" height={24} />
          <ChoiceChip Icon={MailsIcon} label="Mails" to="/mails" height={24} />
          <ChoiceChip
            Icon={InboxIcon}
            label="Sin asignar"
            to="/sin-asignar"
            height={24}
            dot={unassignedPendingCount > 0}
            dotLabel="Hay correos pendientes"
          />
          <ChoiceChip
            Icon={TemplatesIcon}
            label="Templates"
            to="/templates"
            height={24}
          />
          <ChoiceChip
            Icon={DatabaseIcon}
            label="Database"
            to="/database"
            height={24}
          />
          {isSuperAdmin && (
            <ChoiceChip
              Icon={AccountsIcon}
              label="Cuentas"
              to="/admin/cuentas"
              height={24}
            />
          )}
          {isSuperAdmin && (
            <ChoiceChip
              Icon={UsersIcon}
              label="Usuarios"
              to="/admin/usuarios"
              height={24}
            />
          )}
        </div>

        <div className={styles.right} style={{ position: "relative" }}>
          <NotificationsBell
            notifications={notifications}
            unreadCount={unreadCount}
            onNotificationClick={handleNotificationClick}
            onMarkAllAsRead={markAllAsRead}
            onOpenAll={openNotificationsModal}
          />

          <button
            type="button"
            className={styles.avatarButton}
            onClick={() => setSettingsOpen(true)}
            aria-label="Abrir configuración de usuario"
          >
            {userImg && !imgError ? (
              <img
                className={styles.avatarImg}
                alt={displayName}
                src={userImg}
                onError={() => setImgError(true)}
              />
            ) : (
              <div className={styles.avatarFallback}>
                {getInitials(displayName)}
              </div>
            )}
          </button>

          <div
            // Remontar el nodo en cada rotación reinicia la animación de fade.
            key={visibleCreditKpi?.code || "credits-empty"}
            className={`${styles.badge} ${styles.badgeCarousel}`}
            style={
              visibleCreditKpi
                ? { background: CREDIT_CHIP_GRADIENTS[visibleCreditKpi.code] }
                : undefined
            }
            title={
              visibleCreditKpi
                ? visibleCreditKpi.name ||
                  CREDIT_KPI_LABELS[visibleCreditKpi.code]
                : undefined
            }
            onMouseEnter={() => setCarouselPaused(true)}
            onMouseLeave={() => setCarouselPaused(false)}
          >
            <CreditsIcon />
            {visibleCreditKpi == null ? (
              "–"
            ) : (
              <span className={styles.badgeValueStack}>
                {creditKpis.map((kpi) => (
                  <span
                    key={kpi.code}
                    className={
                      kpi.code === visibleCreditKpi.code
                        ? undefined
                        : styles.badgeValueHidden
                    }
                  >
                    {kpi.is_unlimited
                      ? "∞"
                      : (kpi.available ?? 0).toLocaleString()}
                  </span>
                ))}
              </span>
            )}
          </div>
        </div>
      </div>

      <SettingsModal
        isOpen={settingsOpen}
        initialSection={settingsSection}
        onClose={closeSettings}
      />

      {notificationsOpen && (
        <NotificationsModal
          onClose={closeNotificationsModal}
          onOpenNotification={handleNotificationOpenFromModal}
        />
      )}

      {chainFinishedNotification && (
        <RestartChainModal
          notification={chainFinishedNotification}
          onClose={() => setChainFinishedNotification(null)}
        />
      )}
    </>
  );
};

export default Navbar;
